import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companyApi } from "@/services/api";
import {
  FileText,
  Loader2,
  ArrowDownRight,
  ArrowUpRight,
  TrendingUp,
  Wallet,
  ArrowRightLeft,
  DollarSign,
  Filter,
  ChevronLeft,
  ChevronRight,
  Shield,
  ShieldOff,
  Download,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

type TransactionType = "DEPOSIT" | "WITHDRAWAL" | "APPLICATION" | "REDEMPTION_PRINCIPAL" | "REDEMPTION_YIELD" | "CONVERSION_OUT" | "CONVERSION_IN" | "HEDGE_LOCK" | "HEDGE_UNLOCK";
type TransactionDirection = "CREDIT" | "DEBIT";
type TransactionCurrency = "BRL" | "USDC";
type TransactionStatus = "PENDING" | "COMPLETED" | "FAILED" | "CANCELLED";

interface Transaction {
  id: string;
  type: TransactionType;
  direction: TransactionDirection;
  currency: TransactionCurrency;
  status: TransactionStatus;
  amount: number;
  amountFormatted: string;
  description: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const TYPE_LABELS: Record<TransactionType, string> = {
  DEPOSIT: "Depósito",
  WITHDRAWAL: "Saque",
  APPLICATION: "Aplicação",
  REDEMPTION_PRINCIPAL: "Resgate Principal",
  REDEMPTION_YIELD: "Crédito Rendimento",
  CONVERSION_OUT: "Conversão (Saída)",
  CONVERSION_IN: "Conversão (Entrada)",
  HEDGE_LOCK: "Bloqueio Hedge",
  HEDGE_UNLOCK: "Desbloqueio Hedge",
};

const TYPE_OPTIONS = [
  { value: "ALL", label: "Todas" },
  { value: "DEPOSIT", label: "Depósitos" },
  { value: "WITHDRAWAL", label: "Saques" },
  { value: "APPLICATION", label: "Aplicações" },
  { value: "REDEMPTION_PRINCIPAL,REDEMPTION_YIELD", label: "Resgates" },
  { value: "CONVERSION_OUT,CONVERSION_IN", label: "Conversões" },
  { value: "HEDGE_LOCK,HEDGE_UNLOCK", label: "Proteção Cambial" },
];

const CURRENCY_OPTIONS = [
  { value: "ALL", label: "Todas" },
  { value: "BRL", label: "BRLD (Real)" },
  { value: "USDC", label: "USDC (Dólar)" },
];

export function CompanyStatementPage() {
  const [data, setData] = useState<TransactionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [currencyFilter, setCurrencyFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const limit = 20;

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params: {
        page: number;
        limit: number;
        types?: string[];
        currency?: string;
        startDate?: string;
        endDate?: string;
      } = { page, limit };

      if (typeFilter !== "ALL") {
        params.types = typeFilter.split(",");
      }
      if (currencyFilter !== "ALL") {
        params.currency = currencyFilter;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const result = await companyApi.dashboard.getTransactions(params);
      setData(result);
    } catch (error) {
      console.error("Erro ao carregar extrato:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [page, typeFilter, currencyFilter, startDate, endDate]);

  const getEntryIcon = (type: TransactionType, _direction: TransactionDirection, currency: TransactionCurrency) => {
    switch (type) {
      case "DEPOSIT":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <ArrowDownRight className="w-4 h-4 text-green-600" />
          </div>
        );
      case "WITHDRAWAL":
        return (
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
            <ArrowUpRight className="w-4 h-4 text-red-600" />
          </div>
        );
      case "REDEMPTION_YIELD":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
        );
      case "REDEMPTION_PRINCIPAL":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-green-600" />
          </div>
        );
      case "APPLICATION":
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-blue-600" />
          </div>
        );
      case "CONVERSION_OUT":
        return (
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
            <ArrowRightLeft className="w-4 h-4 text-orange-600" />
          </div>
        );
      case "CONVERSION_IN":
        return (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currency === "USDC" ? "bg-blue-100" : "bg-purple-100"}`}>
            {currency === "USDC" ? (
              <DollarSign className="w-4 h-4 text-blue-600" />
            ) : (
              <ArrowRightLeft className="w-4 h-4 text-purple-600" />
            )}
          </div>
        );
      case "HEDGE_LOCK":
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <Shield className="w-4 h-4 text-blue-600" />
          </div>
        );
      case "HEDGE_UNLOCK":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <ShieldOff className="w-4 h-4 text-green-600" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </div>
        );
    }
  };

  const getStatusBadge = (status: TransactionStatus) => {
    switch (status) {
      case "COMPLETED":
        return <Badge variant="success">Confirmado</Badge>;
      case "PENDING":
        return <Badge variant="warning">Pendente</Badge>;
      case "FAILED":
        return <Badge variant="destructive">Falhou</Badge>;
      case "CANCELLED":
        return <Badge variant="secondary">Cancelado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getCurrencyBadge = (currency: TransactionCurrency) => {
    if (currency === "USDC") {
      return <Badge variant="outline" className="text-blue-600 border-blue-300">USDC</Badge>;
    }
    return <Badge variant="outline" className="text-purple-600 border-purple-300">BRLD</Badge>;
  };

  const formatAmount = (_amount: number, direction: TransactionDirection, formatted: string) => {
    const isCredit = direction === "CREDIT";
    return (
      <span className={`font-medium ${isCredit ? "text-green-600" : "text-red-600"}`}>
        {isCredit ? "+" : "-"}{formatted}
      </span>
    );
  };

  const clearFilters = () => {
    setTypeFilter("ALL");
    setCurrencyFilter("ALL");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const hasActiveFilters = typeFilter !== "ALL" || currencyFilter !== "ALL" || startDate || endDate;

  const handleDownloadExtrato = async () => {
    setDownloading(true);
    try {
      const blob = await companyApi.dashboard.getExtratoPdf(startDate || undefined, endDate || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `extrato-aplicacao-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar extrato PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const transactions = data?.transactions || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<FileText className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Extrato"
        description="Movimentação completa da sua conta"
        stats={
          data
            ? [
                { value: data.total.toString(), label: "Transações" },
              ]
            : undefined
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadExtrato}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar Extrato
          </Button>
        }
      />

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtros
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={currencyFilter} onValueChange={(v) => { setCurrencyFilter(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Moeda" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              placeholder="Data inicial"
            />

            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              placeholder="Data final"
            />
          </div>
        </CardContent>
      </Card>

      {/* Extrato */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Movimentação</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma movimentação encontrada</p>
              {hasActiveFilters ? (
                <p className="text-sm mt-1">Tente ajustar os filtros</p>
              ) : (
                <p className="text-sm mt-1">Seu extrato aparecerá aqui após o primeiro depósito</p>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-4 font-medium text-sm">Data</th>
                      <th className="text-left p-4 font-medium text-sm">Descrição</th>
                      <th className="text-left p-4 font-medium text-sm">Moeda</th>
                      <th className="text-left p-4 font-medium text-sm">Status</th>
                      <th className="text-right p-4 font-medium text-sm">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-muted/50">
                        <td className="p-4 text-sm">
                          <div className="flex items-center gap-3">
                            {getEntryIcon(tx.type, tx.direction, tx.currency)}
                            <span>{formatDate(tx.createdAt)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          <div>
                            <span className="font-medium">{TYPE_LABELS[tx.type]}</span>
                            <p className="text-xs text-muted-foreground">{tx.description}</p>
                          </div>
                        </td>
                        <td className="p-4">{getCurrencyBadge(tx.currency)}</td>
                        <td className="p-4">{getStatusBadge(tx.status)}</td>
                        <td className="p-4 text-sm text-right">
                          {formatAmount(tx.amount, tx.direction, tx.amountFormatted)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getEntryIcon(tx.type, tx.direction, tx.currency)}
                        <div>
                          <span className="font-medium text-sm">{TYPE_LABELS[tx.type]}</span>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.createdAt)}</p>
                        </div>
                      </div>
                      {getCurrencyBadge(tx.currency)}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{tx.description}</p>
                    <div className="flex items-center justify-between pt-2 border-t">
                      {formatAmount(tx.amount, tx.direction, tx.amountFormatted)}
                      {getStatusBadge(tx.status)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Página {page} de {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Próxima
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
